import React, { Component } from 'react'
import { compose } from 'redux'
import { extractNumber } from '@cred/shared/utils'
import { fireGtmEvent } from 'utils/gtm'
import PropTypes from 'prop-types'
import querystring from 'querystring'
import config from 'config'
import scriptLoader from 'react-async-script-loader'
import { getVariation, hasEmptyExperiment } from 'utils/experiment'
import { required } from '@cred/shared/validations'
import LendersList from 'components/lenders-list'
import StudentLoanRefiFilterForm from 'components/filter-form'
import withExperiments from 'components/hocs/withExperiments'
import { STUDENT_LOAN_REFI_FILTER_FORM_INITIAL_VALUES as initialValues } from 'common/constants'
import { allLenders } from '../lenders-data'
import { minMaxLoanAmount } from '../validations'

const form = 'rateWidgetForm'

class FullRateTableApp extends Component {
  static propTypes = {
    isScriptLoaded: PropTypes.bool,
    isScriptLoadSucceed: PropTypes.bool,
    utms: PropTypes.shape({
      utm_source: PropTypes.string,
      utm_campaign: PropTypes.string,
      utm_medium: PropTypes.string,
      utm_content: PropTypes.string,
      credclid: PropTypes.string,
      renderId: PropTypes.string,
    }),
    widgetContext: PropTypes.object,
    theme: PropTypes.string,
    experiments: PropTypes.shape({
      activate: PropTypes.func,
      variationMap: PropTypes.object,
      defaultVariationMap: PropTypes.object,
      staticBuild: PropTypes.bool,
    }),
  }

  constructor(props) {
    super(props)
    // In order for the filtering to work, we need to take what will
    // be the dollar amount and turn it into a number ($10,000) -> 10000
    this.state = { ...initialValues, loanAmount: extractNumber(initialValues.loanAmount) }
  }

  render() {
    const { theme } = this.props,
      lenders = this.loadLenders()

    return (
      <>
        <StudentLoanRefiFilterForm
          onCreditChange={this.onChangeCreditStore}
          onAmountChange={this.onChangeLoanAmount}
          initialValues={this.state}
          form={form}
          customValidation={[required, minMaxLoanAmount]}
        />
        <LendersList
          lenders={lenders}
          ctaUrl={this.getCtaUrl()}
          resetFilters={this.resetFilters}
          theme={theme}
          name="StudentLoanRefi"
        />
      </>
    )
  }

  /* Get rates from Rates Update library. */
  loadLenders() {
    const { isScriptLoaded, isScriptLoadSucceed } = this.props,
      { creditScoreApprox, loanAmount } = this.state,
      lenders = [],
      filterFixed = {
        rateType: 'apr',
        vertical: 'refi',
        term: null,
        variable: false,
      },
      filterVariable = {
        rateType: 'apr',
        vertical: 'refi',
        term: null,
        variable: true,
      },
      options = {
        decimals: 2,
        append: '',
        prepend: '',
      }

    /* Get the rates after loading the ratesLibrary JS and do it only once. */
    if (isScriptLoaded && isScriptLoadSucceed) {
      FullRateTableApp
        .filter(allLenders, creditScoreApprox, loanAmount)
        .forEach(lender => {
          const minRateFixed = global.ratesLibrary.getRateValue(
              { lenderSlug: lender.rateSlug, name: 'min', ...filterFixed },
              options,
            ),
            maxRateFixed = global.ratesLibrary.getRateValue(
              { lenderSlug: lender.rateSlug, name: 'max', ...filterFixed },
              options,
            ),
            minRateVar = global.ratesLibrary.getRateValue(
              { lenderSlug: lender.rateSlug, name: 'min', ...filterVariable },
              options,
            ),
            maxRateVar = global.ratesLibrary.getRateValue(
              { lenderSlug: lender.rateSlug, name: 'max', ...filterVariable },
              options,
            )

          /* In case there is any problem searching for rates. */
          if (minRateFixed) {
            lenders.push(
              {
                minRateFixed,
                maxRateFixed,
                minRateVar,
                maxRateVar,
                ...lender,
              },
            )
          }
        })
    }

    return lenders.sort(FullRateTableApp.compareLenders)
  }

  static filter(lenders, creditScore, loanAmount) {
    return lenders
      .filter(lender => !creditScore || lender.minCreditScore <= creditScore)
      .filter(lender => !loanAmount
        || (lender.minAmount <= loanAmount
        && (!lender.maxAmount || lender.maxAmount >= loanAmount)))
  }

  static compareLenders(a, b) {
    return parseFloat(a.minRateFixed) - parseFloat(b.minRateFixed)
  }

  resetFilters = () => {
    this.setState({ ...initialValues, loanAmount: extractNumber(initialValues.loanAmount) })
  }

  onChangeLoanAmount = (e, value) => {
    const gtmValue = { loanAmount: value },
      { widgetContext } = this.props

    this.setState({ loanAmount: extractNumber(value) })

    fireGtmEvent('fieldChange', {
      ...gtmValue,
      ...widgetContext,
    })
  }

  onChangeCreditStore = (e, value) => {
    const intValue = parseInt(value, 10),
      gtmValue = { creditScore: intValue },
      { widgetContext } = this.props

    this.setState({ creditScoreApprox: intValue })

    fireGtmEvent('fieldChange', {
      ...gtmValue,
      ...widgetContext,
    })
  }

  getCtaUrl() {
    const { utms, experiments: { variationMap } } = this.props,
      withoutExperiment = hasEmptyExperiment(variationMap),
      isVariation0 = getVariation(variationMap, '62336686311', 0),
      params = {
        utm_source: utms.utm_source,
        utm_medium: utms.utm_medium,
        utm_campaign: utms.utm_campaign,
        utm_content: utms.utm_content,
        credclid: utms.credclid,
        renderId: utms.renderId,
      },
      ctaUrl = withoutExperiment || isVariation0 ? 'refinance/prequal/signup' : 'refinance-student-loans'

    return `${config.credibleRoot}/${ctaUrl}?${querystring.encode(params)}`
  }
}

// Export named class and functions for unit tests
export { FullRateTableApp, initialValues }

export default compose(
  scriptLoader(config.ratesLibraryUrl),
  withExperiments,
)(FullRateTableApp)
